/**
 * Master Counterparty Detail — Scorecard-style page (P3-10).
 *
 * Header + identity + contacts + cross-references + linked records
 * (POs / GRNs / Invoices / Deals / Payments) + audit log.
 *
 * Route: /admin/counterparties/:id
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Users, Trash2, Save, Plus, Link as LinkIcon, ArrowRight,
  Globe, Mail, Phone, MapPin, FileText, ClipboardList, CheckCircle2,
  Receipt, Briefcase, Package, History,
} from 'lucide-react';

import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import BackButton from '../../../components/admin/BackButton';
import { TypeBadge } from './CounterpartiesList';
import { useToast } from '../../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_OPTIONS = ['supplier', 'client', 'partner', 'lead', 'jv_partner', 'customer', 'contact'];

const CounterpartyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = localStorage.getItem('admin_token');
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [cp, setCp] = useState(null);
  const [links, setLinks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [promoteType, setPromoteType] = useState('');
  const [linkModule, setLinkModule] = useState('suppliers');
  const [linkRecordId, setLinkRecordId] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [cpRes, linksRes] = await Promise.all([
        axios.get(`${API_URL}/api/counterparties/${id}`, auth),
        axios.get(`${API_URL}/api/counterparties/${id}/links`, auth),
      ]);
      setCp(cpRes.data);
      setForm({
        name: cpRes.data.name || '',
        legal_name: cpRes.data.legal_name || '',
        trade_license_number: cpRes.data.trade_license_number || '',
        tax_number: cpRes.data.tax_number || '',
        country: cpRes.data.country || '',
        website: cpRes.data.website || '',
        notes: cpRes.data.notes || '',
      });
      setLinks(linksRes.data);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Failed to load', description: e.message });
    } finally {
      setLoading(false);
    }
  }, [id, auth, toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/api/counterparties/${id}`, form, auth);
      toast({ title: 'Saved' });
      fetchData();
    } catch (e) {
      toast({ variant: 'destructive', title: 'Save failed', description: e.message });
    } finally {
      setSaving(false);
    }
  };

  const handlePromote = async () => {
    if (!promoteType) return;
    try {
      await axios.post(`${API_URL}/api/counterparties/${id}/promote`, { add_type: promoteType }, auth);
      toast({ title: 'Type added' });
      setPromoteType('');
      fetchData();
    } catch (e) {
      toast({ variant: 'destructive', title: 'Promote failed', description: e.message });
    }
  };

  const handleLink = async () => {
    if (!linkRecordId) return;
    try {
      await axios.post(`${API_URL}/api/counterparties/${id}/link`, {
        module: linkModule, record_id: linkRecordId,
      }, auth);
      toast({ title: 'Linked' });
      setLinkRecordId('');
      fetchData();
    } catch (e) {
      toast({ variant: 'destructive', title: 'Link failed', description: e.message });
    }
  };

  const handleUnlink = async (xref) => {
    if (!window.confirm(`Unlink ${xref.module}/${xref.record_id}?`)) return;
    try {
      await axios.delete(
        `${API_URL}/api/counterparties/${id}/link?module=${xref.module}&record_id=${xref.record_id}`,
        auth,
      );
      toast({ title: 'Unlinked' });
      fetchData();
    } catch (e) {
      toast({ variant: 'destructive', title: 'Unlink failed', description: e.message });
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Soft-delete this counterparty?')) return;
    try {
      await axios.delete(`${API_URL}/api/counterparties/${id}`, auth);
      toast({ title: 'Deleted' });
      navigate('/admin/counterparties');
    } catch (e) {
      toast({ variant: 'destructive', title: 'Delete failed', description: e.message });
    }
  };

  if (loading || !cp) {
    return (
      <div className="p-6"><BackButton /><div className="mt-6 text-slate-500">Loading…</div></div>
    );
  }

  return (
    <div className="p-6 space-y-4" data-testid="counterparty-detail-page">
      <BackButton />

      {/* Header */}
      <Card>
        <CardContent className="p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Master Counterparty</p>
              <h1 className="mt-1 text-2xl font-bold text-slate-900 truncate" data-testid="cp-name">{cp.name}</h1>
              {cp.legal_name && <p className="text-sm text-slate-500">{cp.legal_name}</p>}
              <div className="mt-2 flex flex-wrap gap-1">
                {(cp.types || []).map((t) => <TypeBadge key={t} type={t} />)}
                {(cp.types || []).length === 0 && (
                  <span className="text-xs text-slate-400">No type set — add one below</span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Select value={promoteType} onValueChange={setPromoteType}>
                <SelectTrigger className="h-9 w-[150px]" data-testid="cp-promote-select">
                  <SelectValue placeholder="Add type…" />
                </SelectTrigger>
                <SelectContent>
                  {TYPE_OPTIONS.filter((t) => !(cp.types || []).includes(t)).map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                onClick={handlePromote}
                disabled={!promoteType}
                className="bg-[#FF6B35] text-white"
                data-testid="cp-promote-btn"
              >
                <ArrowRight className="h-3.5 w-3.5 mr-1" />
                Promote
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleDelete}
                className="text-red-600 border-red-300 hover:bg-red-50"
                data-testid="cp-delete-btn"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Identity */}
      <Card>
        <CardContent className="p-5">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">Identity</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cp-input-name" />
            </div>
            <div>
              <Label className="text-xs">Legal name</Label>
              <Input value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Country</Label>
              <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Trade license</Label>
              <Input value={form.trade_license_number} onChange={(e) => setForm({ ...form, trade_license_number: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Tax number / TRN</Label>
              <Input value={form.tax_number} onChange={(e) => setForm({ ...form, tax_number: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Website</Label>
              <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
            </div>
            <div className="md:col-span-3">
              <Label className="text-xs">Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <Button onClick={handleSave} disabled={saving} className="bg-[#023E8A] text-white" data-testid="cp-save-btn">
              <Save className="h-4 w-4 mr-1" />
              {saving ? 'Saving…' : 'Save identity'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Contacts + channels */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Card data-testid="cp-emails-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Mail className="h-4 w-4 text-slate-500" />
              <h3 className="text-sm font-semibold text-slate-800">Emails</h3>
            </div>
            <ul className="space-y-1 text-sm">
              {(cp.emails || []).map((e, i) => (
                <li key={i} className="text-slate-700">
                  {e.address}
                  {e.is_primary && <span className="ml-1 text-[10px] text-emerald-600 font-semibold">PRIMARY</span>}
                </li>
              ))}
              {!(cp.emails || []).length && <li className="text-xs text-slate-400">No emails</li>}
            </ul>
          </CardContent>
        </Card>

        <Card data-testid="cp-phones-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Phone className="h-4 w-4 text-slate-500" />
              <h3 className="text-sm font-semibold text-slate-800">Phones</h3>
            </div>
            <ul className="space-y-1 text-sm">
              {(cp.phones || []).map((p, i) => (
                <li key={i} className="text-slate-700">
                  {p.number}
                  {p.is_primary && <span className="ml-1 text-[10px] text-emerald-600 font-semibold">PRIMARY</span>}
                </li>
              ))}
              {!(cp.phones || []).length && <li className="text-xs text-slate-400">No phones</li>}
            </ul>
          </CardContent>
        </Card>

        <Card data-testid="cp-addresses-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="h-4 w-4 text-slate-500" />
              <h3 className="text-sm font-semibold text-slate-800">Addresses</h3>
            </div>
            <ul className="space-y-1 text-xs">
              {(cp.addresses || []).map((a, i) => (
                <li key={i} className="text-slate-700">
                  {[a.label && `[${a.label}]`, a.street, a.city, a.postal_code, a.country].filter(Boolean).join(', ')}
                </li>
              ))}
              {!(cp.addresses || []).length && <li className="text-slate-400">No addresses</li>}
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Cross-references */}
      <Card data-testid="cp-xrefs-card">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <LinkIcon className="h-4 w-4 text-slate-500" />
              <h3 className="text-sm font-semibold text-slate-800">Cross-references</h3>
              <span className="text-xs text-slate-500">
                {(cp.cross_references || []).length} linked module records
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Select value={linkModule} onValueChange={setLinkModule}>
                <SelectTrigger className="h-8 w-[160px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="suppliers">suppliers</SelectItem>
                  <SelectItem value="clients">clients</SelectItem>
                  <SelectItem value="contacts">contacts</SelectItem>
                  <SelectItem value="financial_customers">financial_customers</SelectItem>
                  <SelectItem value="leads">leads</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder="Record UUID"
                value={linkRecordId}
                onChange={(e) => setLinkRecordId(e.target.value)}
                className="h-8 w-[240px]"
                data-testid="cp-link-record-input"
              />
              <Button size="sm" onClick={handleLink} className="bg-[#023E8A] text-white" data-testid="cp-link-btn">
                <Plus className="h-3.5 w-3.5 mr-1" />
                Link
              </Button>
            </div>
          </div>
          {(cp.cross_references || []).length === 0 ? (
            <p className="text-xs text-slate-400">No records linked yet</p>
          ) : (
            <ul className="space-y-1">
              {(cp.cross_references || []).map((x, i) => (
                <li key={i} className="flex items-center justify-between rounded border border-slate-100 bg-slate-50 px-3 py-2 text-xs">
                  <div>
                    <span className="font-semibold text-slate-800">{x.module}</span>
                    <span className="mx-2 text-slate-400">·</span>
                    <span className="font-mono text-slate-600">{x.record_id}</span>
                    {x.linked_at && <span className="ml-3 text-slate-400">{new Date(x.linked_at).toLocaleDateString()}</span>}
                  </div>
                  <Button size="sm" variant="ghost" className="h-6 px-2 text-red-500" onClick={() => handleUnlink(x)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Linked records aggregator */}
      <Card data-testid="cp-linked-records">
        <CardContent className="p-5">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">Linked module records</h3>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4">
            {[
              { key: 'purchase_orders', label: 'POs', icon: Package },
              { key: 'grns', label: 'GRNs', icon: CheckCircle2 },
              { key: 'supplier_invoices', label: 'Supplier Inv.', icon: FileText },
              { key: 'client_invoices', label: 'Client Inv.', icon: Receipt },
              { key: 'deals', label: 'Deals', icon: Briefcase },
              { key: 'payments', label: 'Payments', icon: ClipboardList },
            ].map((b) => {
              const Icon = b.icon;
              const count = links?.counts?.[b.key] || 0;
              return (
                <div key={b.key} className="rounded border border-slate-200 bg-white p-3" data-testid={`cp-count-${b.key}`}>
                  <div className="flex items-center gap-1 text-xs text-slate-500">
                    <Icon className="h-3.5 w-3.5" />
                    {b.label}
                  </div>
                  <p className="mt-0.5 text-2xl font-bold text-[#023E8A]">{count}</p>
                </div>
              );
            })}
          </div>

          {(links?.links?.purchase_orders || []).length > 0 && (
            <div className="mb-4">
              <h4 className="text-xs font-semibold text-slate-700 mb-1">Recent Purchase Orders</h4>
              <ul className="space-y-1">
                {links.links.purchase_orders.slice(0, 5).map((p) => (
                  <li key={p.id}
                      className="flex items-center justify-between text-xs border-b border-slate-100 py-1 hover:bg-slate-50 cursor-pointer"
                      onClick={() => navigate(`/admin/procurement/purchase-orders/${p.id}`)}>
                    <span className="font-medium">{p.po_number}</span>
                    <span className="text-slate-500">{p.status}</span>
                    <span className="font-semibold">{p.currency} {Number(p.total_amount || 0).toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(links?.links?.grns || []).length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-slate-700 mb-1">Recent GRNs</h4>
              <ul className="space-y-1">
                {links.links.grns.slice(0, 5).map((g) => (
                  <li key={g.id}
                      className="flex items-center justify-between text-xs border-b border-slate-100 py-1 hover:bg-slate-50 cursor-pointer"
                      onClick={() => navigate(`/admin/procurement/grns/${g.id}`)}>
                    <span className="font-medium">{g.grn_number}</span>
                    <span className="text-slate-500">{g.status}</span>
                    <span className="text-slate-400">{(g.received_date || '').slice(0, 10)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Audit log */}
      <Card data-testid="cp-audit-card">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <History className="h-4 w-4 text-slate-500" />
            <h3 className="text-sm font-semibold text-slate-800">Audit log</h3>
            <span className="text-xs text-slate-500">{(cp.audit_log || []).length} entries</span>
          </div>
          <ul className="space-y-1 text-xs">
            {(cp.audit_log || []).slice().reverse().slice(0, 10).map((a, i) => (
              <li key={i} className="flex items-start gap-2 border-b border-slate-100 py-1">
                <span className="font-mono text-slate-400 w-24 shrink-0">{a.action}</span>
                <span className="flex-1 text-slate-700">{a.actor || 'system'}</span>
                <span className="text-slate-400">{a.timestamp ? new Date(a.timestamp).toLocaleString() : ''}</span>
              </li>
            ))}
            {!(cp.audit_log || []).length && <li className="text-slate-400">No audit entries</li>}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default CounterpartyDetail;
